import { useQuery } from '@apollo/client';
import type { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  GetMyItemsDashboardDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { DashboardItemProps } from 'src/components/register-dashboard/DashboardItem';
import { DashboardItem } from 'src/components/register-dashboard/DashboardItem';
import RegisterDashboard from 'src/components/register-dashboard/RegisterDashboard';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useChangeRequests } from '@/hooks/useChangeRequests';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { publicPoliciesUrl, requestsRegisterUrl } from '@/utils/urls';

import useGetClickthroughEnabled from './hooks/useGetClickthroughEnabled';
import { useGetQueryVariables } from './hooks/useGetQueryVariables';
import { useGetRibbonItemVisibilities } from './hooks/useGetRibbonItemVisibilities';
import { useGetStatusFilterOptions } from './hooks/useGetStatusFilterOptions';
import { useGetTypeFilterOptions } from './hooks/useGetTypeFilterOptions';
import { useFlattenRibbonData } from './useFlattenRibbonData';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';
import { useRibbonNavigation } from './useRibbonNavigation';

const DEFAULT_FILTER_QUERY: PropertyFilterQuery = {
  tokens: [],
  operation: 'and',
};

// Filtering properties for the ribbon's "Edit ribbon" modal.
// propertyLabel strings mirror the TableFields headers in config.tsx.
const FILTERING_PROPERTIES = [
  {
    key: 'StatusLabelled',
    propertyLabel: 'Object Status',
    groupValuesLabel: 'Object Status values',
    operators: ['=', '!=', ':', '!:'] as const,
  },
  {
    key: 'Type',
    propertyLabel: 'Type',
    groupValuesLabel: 'Type values',
    operators: ['=', '!=', ':', '!:'] as const,
  },
];

const Ribbon: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.myItemsDashboard.ribbonTitles',
  });
  const { t: st } = useTranslation(['common'], { keyPrefix: 'dashboard' });
  const itemVisibilities = useGetRibbonItemVisibilities();

  const noClickthroughMessageContent = useMemo(
    () => st('my_items_filters_alert'),
    [st]
  );

  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const { isActiveApprover } = useChangeRequests();
  const clickThroughEnabled = useGetClickthroughEnabled();

  const queryVars = useGetQueryVariables();

  const { data } = useQuery(GetMyItemsDashboardDocument, {
    variables: queryVars,
    fetchPolicy: 'no-cache',
    onError: (error) => {
      addNotification({
        type: 'error',
        content: <>{error.message}</>,
      });
    },
  });

  // Tracks which tile is visually selected (highlighted)
  const [propertyFilterQuery, setPropertyFilterQuery] =
    useState<PropertyFilterQuery>(DEFAULT_FILTER_QUERY);

  const ribbonItems = useFlattenRibbonData(data);
  const statusOptions = useGetStatusFilterOptions();
  const typeOptions = useGetTypeFilterOptions();

  const filteringOptions = useMemo(
    // Both Status and Type options are fixed vocabularies (every possible
    // entity's statuses and every supported Type label) so admins can
    // select, remove, and re-add tokens freely even when no matching items
    // are currently loaded — see useGetStatus/TypeFilterOptions.
    () => [...statusOptions, ...typeOptions],
    [statusOptions, typeOptions]
  );

  const { ribbonProps } = useRibbonAndExport(useGetDefaultRibbonFilters);

  // Navigates to the entity register page matching the Type token in the query.
  // Also updates local state so the clicked tile stays highlighted.
  const navigateOnTileClick = useRibbonNavigation(clickThroughEnabled, data);
  const handleTileClick = (query: PropertyFilterQuery) => {
    if (!clickThroughEnabled) {
      return;
    }
    setPropertyFilterQuery(query);
    navigateOnTileClick(query);
  };

  const clickThrough = (navigateFn: () => Promise<void> | void) => {
    if (clickThroughEnabled) {
      return () => navigateFn();
    }
  };

  return (
    <div className={'flex gap-3'}>
      {itemVisibilities.approvals || itemVisibilities.attestations ? (
        <RegisterDashboard>
          <ConditionalDashboardItem
            show={itemVisibilities.approvals}
            title={t('approvals')}
            value={
              data?.change_request.filter((cr) =>
                isActiveApprover(
                  cr,
                  cr.currentUserOwnerList?.map((u) => u.UserId ?? '')
                )
              ).length ?? 0
            }
            onClick={clickThrough(() =>
              navigate(
                requestsRegisterUrl({
                  tokens: [
                    {
                      propertyKey: 'RequiresAction',
                      value: 'true',
                      operator: '=',
                    },
                  ],
                  operation: 'and',
                })
              )
            )}
            noClickthroughMessageContent={noClickthroughMessageContent}
          />
          {itemVisibilities.approvals && itemVisibilities.attestations && (
            <div className={'self-stretch bg-grey200 !w-1 !flex-none'} />
          )}
          <ConditionalDashboardItem
            show={itemVisibilities.attestations}
            title={t('attestations')}
            value={data?.attestation_record_aggregate.aggregate?.count ?? 0}
            onClick={clickThrough(() =>
              navigate(
                publicPoliciesUrl({
                  filtering: {
                    tokens: [
                      {
                        propertyKey: 'AttestationStatusLabel',
                        value: 'Pending',
                        operator: '=',
                      },
                    ],
                    operation: 'and',
                  },
                })
              )
            )}
            noClickthroughMessageContent={noClickthroughMessageContent}
          />
        </RegisterDashboard>
      ) : (
        <></>
      )}
      <div data-testid={'my-items-ribbon'} className={'min-w-0 flex-1'}>
        <CustomisableRibbon
          items={ribbonItems}
          propertyFilterQuery={propertyFilterQuery}
          onFilterQueryChanged={handleTileClick}
          filteringProperties={FILTERING_PROPERTIES}
          filteringOptions={filteringOptions}
          parentType={Parent_Type_Enum.MyItems}
          clickThroughEnabled={clickThroughEnabled}
          noClickthroughMessageContent={noClickthroughMessageContent}
          // Match RegisterDashboard's inner padding-bottom so the two ribbon
          // boxes align when the right side has no overflow scrollbar.
          innerClassName={'pb-3'}
          {...ribbonProps}
          // Custom ribbon editing is not yet released for My Items — hide the
          // edit-ribbon icon even for users with custom_ribbon permissions.
          showEditButton={false}
        />
      </div>
    </div>
  );
};

const ConditionalDashboardItem: FC<DashboardItemProps & { show: boolean }> = ({
  show,
  ...props
}) => {
  if (!show) {
    return <></>;
  }

  return <DashboardItem {...props} />;
};

export default Ribbon;
